/**
 * Initializes the filter binding logic between form controls (input, select, checkbox, radio)
 * and a container for displaying active filters. Uses `data-bind-filters-for="outputId"` on the inputs,
 * `data-reset-filters-for="outputId"` on reset buttons, and `data-load-filters-for` to define how to call `load()`.
 */
window.bindFiltersControls = function bindFiltersControls() {
  const bindFieldsArr = document.querySelectorAll('[data-bind-filters-for]');
  if (!bindFieldsArr.length) return;

  class FiltersControl {
    constructor() {
      /**
       * A map that links each outputId to its related fields.
       * @type {Map<string, HTMLElement[]>}
       */
      this.outputMap = new Map();

      /**
       * Tracks whether the `load()` function was already triggered once per output.
       * @type {Map<string, boolean>}
       */
      this.countLoad = new Map();

      /**
       * Set of outputIds where filter rendering should happen only on user click, not automatically.
       * @type {Set<string>}
       */
      this.delayedRenderMap = new Set();

      /**
       * Stores debounce timers for individual input fields.
       * @type {WeakMap<HTMLElement, number>}
       */
      this.debounceTimers = new WeakMap();

      this.init();
    }

    /**
     * Initializes all filter bindings and sets up DOM event listeners.
     */
    init() {
      this.groupFieldsByOutput();

      // console.log(this.outputMap);

      this.outputMap.forEach((fields, outputId) => {
        const container = document.getElementById(outputId);
        if (!container) return;

        const showFiltersButton = document.querySelector(
          `[data-show-filters-click-for="${outputId}"]`
        );

        // Optional: Delay rendering until user clicks the button
        if (showFiltersButton) {
          this.delayedRenderMap.add(outputId);

          showFiltersButton.addEventListener('click', () => {
            // Render only (without load call)
            this.renderFilters(fields, container, { skipLoad: true });
          });
        }

        // Attach listeners to each input/select/checkbox/radio
        fields.forEach((field) => {
          if (field.tagName === 'INPUT' && field.type === 'text') {
            field.addEventListener('input', () => {
              if (!this.delayedRenderMap.has(outputId)) {
                this.debounce(
                  field,
                  () => this.renderFilters(fields, container),
                  500
                );
              }
            });
          } else {
            field.addEventListener('change', () => {
              if (!this.delayedRenderMap.has(outputId)) {
                this.renderFilters(fields, container);
              }
            });
          }
        });

        // Click on filter element removes it
        container.addEventListener('click', (e) => {
          const filter = e.target.closest('.meta-filter');
          if (!filter) return;

          const fieldName = filter.dataset.fieldName;
          const fieldValue = filter.dataset.fieldValue;

          const fieldToReset = fields.find(
            (f) => f.name === fieldName || f.id === fieldName
          );
          if (fieldToReset) {
            this.resetField(fieldToReset, fieldValue);
            this.renderFilters(fields, container);
          }
        });

        // Support reset button for clearing all filters
        const resetButton = document.querySelector(
          `[data-reset-filters-for="${outputId}"]`
        );
        if (resetButton) {
          resetButton.addEventListener('click', () => {
            fields.forEach((field) => this.resetField(field));
            this.renderFilters(fields, container);
          });
        }

        // Initial render
        this.renderFilters(fields, container);
      });
    }

    /**
     * Groups all fields by their `data-bind-filters-for` value.
     */
    groupFieldsByOutput() {
      this.outputMap.clear();

      document.querySelectorAll('[data-bind-filters-for]').forEach((field) => {
        const outputId = field.dataset.bindFiltersFor;
        if (!this.outputMap.has(outputId)) {
          this.outputMap.set(outputId, []);
          this.countLoad.set(outputId, false);
        }
        this.outputMap.get(outputId).push(field);
      });
    }

    /**
     * Extracts a displayable label from a given field (input/select/checkbox/radio).
     * Returns null if the field should not generate a filter (e.g., no value).
     *
     * @param {HTMLElement} field - The form field element.
     * @returns {string|null} - The text to display in the filter badge.
     */
    getFieldValueLabel(field) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        if (!field.checked) return null;
        return field.dataset.label || field.value;
      }

      if (field.tagName === 'SELECT') {
        const selected = Array.from(field.selectedOptions).filter(
          (opt) => opt.value !== ''
        );
        if (!selected.length) return null;
        return selected.map((opt) => opt.textContent.trim()).join(', ');
      }

      const trimmed = field.value?.trim();
      return trimmed ? trimmed : null;
    }

    /**
     * Renders all active filters into the provided container.
     * Shows placeholders if no filters exist.
     *
     * @param {HTMLElement[]} fields - The bound fields.
     * @param {HTMLElement} container - The target container.
     * @param {Object} [options]
     * @param {boolean} [options.skipLoad=false] - If true, skip triggering `load()`.
     */
    renderFilters(fields, container, options = {}) {
      const { skipLoad = false } = options;
      container.innerHTML = '';
      const parentHiddenEl = container.closest(
        '[data-parent-output-filters-hidden]'
      );

      let hasFilters = false;

      fields.forEach((field) => {
        const valueLabel = this.getFieldValueLabel(field);
        if (!valueLabel) return;

        hasFilters = true;
        const fieldName = field.name || field.id;

        const filterEl = document.createElement('div');
        filterEl.className = 'meta-filter';
        filterEl.dataset.fieldName = fieldName;
        filterEl.dataset.fieldValue = field.value;

        filterEl.innerHTML = `
          <div class="meta-filter__btn">
            <i class="icon-close-checkbox"></i>
          </div>
          <div class="meta-filter__text">${valueLabel}</div>
        `;

        container.appendChild(filterEl);
      });

      // Show placeholder if no filters
      if (!hasFilters) {
        if (parentHiddenEl) {
          parentHiddenEl.classList.add('hidden');
        } else {
          const placeholderText = container.dataset.outputFiltersPlaceholder;
          const placeholder = document.createElement('div');
          placeholder.className = 'meta-filter-placeholder';
          if (placeholderText) {
            placeholder.textContent = placeholderText;
          } else {
            placeholder.textContent = 'Select filters...';
          }
          container.appendChild(placeholder);
        }
      } else if (parentHiddenEl) {
        parentHiddenEl.classList.remove('hidden');
      }

      const outputId = [...this.outputMap.entries()].find(([, list]) =>
        list.some((el) => fields.includes(el))
      )?.[0];

      if (outputId && !skipLoad) {
        const hasLoadedBefore = this.countLoad.get(outputId);
        if (hasLoadedBefore) {
          clearTimeout(this.loadTimer);
          this.loadTimer = setTimeout(() => this.loadResult(), 0);
        } else {
          this.countLoad.set(outputId, true);
        }
      }

      // console.log(this.outputMap);
    }

    /**
     * Resets the value of the given field (input, select, checkbox, radio).
     * Optionally clears only a specific value (used when removing a filter).
     *
     * @param {HTMLElement} field - The field to reset.
     * @param {string} [valueToClear] - Optional value to specifically clear.
     */
    resetField(field, valueToClear) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = false;
      } else if (field.tagName === 'SELECT') {
        Array.from(field.options).forEach((opt) => {
          if (!valueToClear || opt.value === valueToClear) {
            opt.selected = false;
          }
        });

        if (field.multiple) {
          field.value = '';
        } else {
          field.selectedIndex = 0;
        }
      } else {
        field.value = '';
      }

      // Trigger change event to update bindings
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Debounces execution of a callback function.
     *
     * @param {HTMLElement} field - The field to associate the debounce with.
     * @param {Function} callback - The callback to run after the delay.
     * @param {number} delay - Delay in milliseconds.
     */
    debounce(field, callback, delay = 500) {
      if (this.debounceTimers.has(field)) {
        clearTimeout(this.debounceTimers.get(field));
      }

      const timer = setTimeout(() => {
        callback();
        this.debounceTimers.delete(field);
      }, delay);

      this.debounceTimers.set(field, timer);
    }

    /**
     * Calls the global `load()` function with arguments specified
     * in elements using `data-load-filters-for` and `data-load-filters`.
     */
    loadResult() {
      const arr = Array.from(this.outputMap);

      for (let i = 0; i < arr.length; i++) {
        const id = arr[i][0];

        const forFieldsLoad = document.querySelector(
          `[data-load-filters-for="${id}"]`
        );

        if (!forFieldsLoad) continue;

        const rawValue = forFieldsLoad.dataset.loadFilters;
        if (!rawValue) continue;

        try {
          const args = Function(`return [${rawValue}]`)();
          console.log('Parsed args for load():', args);

          load(...args);
        } catch (err) {
          console.error('Invalid data-load-filters:', rawValue, err);
        }
      }
    }
  }

  // const test = new FiltersControl();
  // console.log(test.init);
};

bindFiltersControls();
